import React, { useCallback, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { escapeRegExp, isEmpty, isNil, startsWith } from 'lodash'
import { IconMessagePlus, IconPlayerPlay } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { Pill } from '@/components/pills/Pill'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import SelectionCommentPopover from '@/components/comments/SelectionCommentPopover'
import { renderTextWithMarks } from '@/components/comments/renderHighlightedText'
import { locateSnippet } from '@/components/comments/anchorSnippet'
import { convertAgentNameToInitials, formatDuration } from '@/utils/helpers'

import { useTranscript } from './TranscriptContext'

export const ConversationBlock = ({
  side,
  data,
  showHypothesis,
  highlighted = false,
  searchQuery = '',
  hardSelectedEvent,
  metadata,
  searchMatches = [],
  currentSearchIndex = -1,
  blockIndex,
  matchIndicesMap = {},
  isTranslating = false,
  comments = null,
}) => {
  const initials = convertAgentNameToInitials(metadata.agent_name)
  const conversationRef = useRef(null)
  // The selection being commented on, lifted from the popover so it stays
  // painted while composing (the native selection collapses on textarea focus).
  const [draftRange, setDraftRange] = useState(null)
  // Step one of commenting: the hover affordance was pressed, so this turn's
  // text is now selectable and the user is being asked to pick the span.
  const [armed, setArmed] = useState(false)
  // Step two landed and the composer is up.
  const [composerOpen, setComposerOpen] = useState(false)
  // Any comment interaction on this turn — selection, pill or composer. Drives
  // the turn's own "you are commenting on this" highlight.
  const [commentActive, setCommentActive] = useState(false)
  // Freeze playback auto-scroll for the duration of a comment interaction on this
  // turn. useCallback keeps the identity stable so the popover's report effect
  // doesn't re-fire every render.
  const { setBlockCommenting, setBlockComposing, isComposingComment } = useTranscript()
  const handleCommentActiveChange = useCallback(
    (active) => {
      setCommentActive(active)
      setBlockCommenting(blockIndex, active)
    },
    [blockIndex, setBlockCommenting]
  )
  const handleCommentComposingChange = useCallback((composing) => setComposerOpen(composing), [])
  const handleDisarm = useCallback(() => setArmed(false), [])
  // The comment flow owns the page from the moment the affordance is pressed —
  // not just once the composer is up. Picking the span is part of writing the
  // comment, and it is the step the audio running on would most get in the way
  // of, so the player pauses and locks here (AVR-875).
  const commentFlowActive = armed || composerOpen
  useEffect(() => {
    setBlockComposing(blockIndex, commentFlowActive)
    // Hide Transcript unmounts this block while the provider stays mounted, so
    // without releasing the key here the player would stay locked with no
    // comment UI left to cancel it.
    return () => setBlockComposing(blockIndex, false)
  }, [blockIndex, commentFlowActive, setBlockComposing])
  // One comment at a time. The flow no longer ends on click-away, so without
  // this a second turn's trigger would start a second one on top of it. While
  // one is running every other turn's comment layer stands down — no trigger,
  // no selection — which is also what makes the open comment read as the thing
  // the page is on.
  const otherTurnComposing = isComposingComment && !commentFlowActive

  const highlightText = (text) => {
    try {
      if (!searchQuery) return text

      const regex = new RegExp(`(${escapeRegExp(searchQuery)})`, 'gi')

      const blockMatchIndices = matchIndicesMap[blockIndex] || []
      const currentMatchGlobalIndex = searchMatches[currentSearchIndex]

      const currentMatchLocalIndex = blockMatchIndices.findIndex(
        (index) => index === currentMatchGlobalIndex
      )

      let matchCount = 0

      return text.split(regex).map((part, i) => {
        const key = `${part.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${i}`

        if (regex.test(part)) {
          // Check if this specific match should be highlighted as current
          const isCurrentMatch = matchCount === currentMatchLocalIndex
          matchCount++

          return (
            <mark
              key={key}
              className={classNames('transcript-highlight', { 'current-match': isCurrentMatch })}
            >
              {part}
            </mark>
          )
        }
        return part
      })
    } catch (error) {
      return text
    }
  }

  const firstTimestamp = data[0].timestamp
  const blockStartTime = formatDuration(firstTimestamp)
  const formattedTimestamp = startsWith(blockStartTime, '00:')
    ? blockStartTime.slice(3)
    : blockStartTime
  // older call transcripts may not have hypothesis available
  const hasHypothesis = Boolean(data.find((snippet) => snippet.hyp?.length > 0))
  const fullText = data.map((snippet) => snippet.text).join(' ')
  const highlightedParts = highlightText(fullText)

  // `comments` is an optional Call-Explorer config (absent on the plain transcript
  // view); normalize once so every comment-layer access below is null-safe.
  const commentsConfig = comments || {}
  const commentItems = commentsConfig.items || []
  // Voice-agent comments anchor to assistant turns only (side 0). turnStartMs is
  // the turn's first-snippet timestamp in ms — the same key the BE derives from
  // pretty_transcript blocks, so it must match the seconds→ms convention there.
  const commentsEnabled = Boolean(comments) && commentsConfig.enabled !== false && side === 0
  const turnStartMs = Math.round(firstTimestamp * 1000)
  // Re-anchor each comment by its snippet in the live turn text (offset = tie-
  // breaker); a comment whose snippet is gone resolves to null and isn't painted.
  const turnMarks =
    commentsEnabled && !isEmpty(commentItems)
      ? commentItems
          .filter((comment) => comment.turn?.startMs === turnStartMs && comment.highlight)
          .map((comment) => {
            const range = locateSnippet(
              fullText,
              comment.highlight.snippet,
              comment.highlight.start
            )
            return range
              ? {
                  id: comment.id,
                  start: range.start,
                  end: range.end,
                  active: comment.id === commentsConfig.focusedId,
                }
              : null
          })
          .filter(Boolean)
      : []
  // Selection-commenting and comment marks share the turn text; suppress them
  // while a search is active so the two <mark> systems don't fight over the DOM.
  const showCommentLayer = commentsEnabled && isEmpty(searchQuery)
  // Translating swaps the turn text for a skeleton, so there is nothing to
  // select and no container for the popover to measure.
  const canComment = showCommentLayer && !isTranslating

  // Searching or translating takes the comment layer away mid-flow. The flow's
  // flags have to go with it: the popover unmounts, but `armed` lives here, so
  // without this the turn stays armed behind the search results — and the
  // player stays locked, because the lock follows these flags.
  useEffect(() => {
    if (!canComment) {
      setArmed(false)
      setComposerOpen(false)
    }
  }, [canComment])

  // Index 0 carries the whole turn's text; later snippet spans exist only to
  // anchor their timestamp (e.g. the event-trigger marker). Paint comment marks
  // (or, when searching, the search highlights) onto that text while keeping the
  // per-snippet spans intact, so enabling commenting doesn't drop the
  // event-trigger tooltip on assistant turns.
  // The pending selection paints as a non-interactive draft mark alongside the
  // saved-comment marks; both index into fullText, so no remapping is needed.
  const renderMarks =
    showCommentLayer && !isNil(draftRange)
      ? [...turnMarks, { id: 'draft', start: draftRange.start, end: draftRange.end, draft: true }]
      : turnMarks
  const turnTextContent = showCommentLayer
    ? renderTextWithMarks(fullText, renderMarks, {
        onMarkClick: commentsConfig.onMarkClick,
        className: 'comment-highlight comment-highlight--transcript',
      })
    : highlightedParts

  const conversation = data.map((snippet, index) => {
    const { timestamp: snippetTimestamp, text: snippetText } = snippet
    const matchesTimestamp = hardSelectedEvent?.timestamp === snippetTimestamp

    return (
      <span
        // eslint-disable-next-line react/no-array-index-key
        key={`transcript-${snippetTimestamp}-${snippetText}-${index}`}
        data-timestamp={`transcript-${snippetTimestamp}`}
      >
        {index === 0 ? turnTextContent : null}{' '}
        {matchesTimestamp && (
          <InteractiveTooltip
            content={
              <>
                <em>{hardSelectedEvent?.displayText}</em> event triggered
              </>
            }
          >
            <span
              data-testid="transcript-event-trigger"
              id="transcript-event-marker"
              className="transcript-event-trigger"
            />
          </InteractiveTooltip>
        )}
      </span>
    )
  })

  const hypothesis = data.map((snippet) => {
    return (
      <span
        key={`hypothesis-${snippet.timestamp}`}
        data-timestamp={`hypothesis-${snippet.timestamp}`}
        className={classNames({ 'is-diff': snippet.is_diff })}
      >
        {snippet.hyp}{' '}
      </span>
    )
  })

  const generateSkeletonBars = () => {
    const textLength = fullText.length
    const SHORT_TEXT_THRESHOLD = 50
    const MEDIUM_TEXT_THRESHOLD = 150

    if (textLength < SHORT_TEXT_THRESHOLD) {
      return [<div key="bar-1" className="shine" style={{ width: '35%' }} />]
    }
    if (textLength < MEDIUM_TEXT_THRESHOLD) {
      return [
        <div key="bar-1" className="shine" style={{ width: '80%' }} />,
        <div key="bar-2" className="shine" style={{ width: '40%' }} />,
      ]
    }
    return [
      <div key="bar-1" className="shine" style={{ width: '95%' }} />,
      <div key="bar-2" className="shine" style={{ width: '90%' }} />,
      <div key="bar-3" className="shine" style={{ width: '45%' }} />,
    ]
  }

  return (
    <div className="transcript-block" key={firstTimestamp}>
      <div className="transcript-side-container">
        {(side === 1 || side === 0) && (
          <div
            className={classNames('transcript-side', { customer: side === 1, agent: side === 0 })}
          >
            <div>
              {side === 1 ? (
                <Pill small circular emphasized />
              ) : (
                <Pill small circular brand>
                  {initials}
                </Pill>
              )}
            </div>
            <div>{side === 1 ? 'Customer' : metadata.agent_name}</div>
            <div className="transcript-start-time">
              <div>{formattedTimestamp}</div>
              <IconPlayerPlay />
            </div>
            {canComment && !isComposingComment && (
              // Same elevated pill the selection affordance uses — this is that
              // button, moved to the front of the flow.
              <span className="transcript-comment-trigger comment-selection-popover__toolbar">
                <Button
                  basic
                  circular
                  small
                  icon={<IconMessagePlus />}
                  tooltip="Comment on this turn"
                  aria-label="Comment on this turn"
                  data-testid={`transcript-comment-trigger-${turnStartMs}`}
                  onClick={(event) => {
                    // The turn is clickable to seek; commenting must not also
                    // move the playhead.
                    event.stopPropagation()
                    setArmed(true)
                  }}
                />
              </span>
            )}
          </div>
        )}
        {isTranslating ? (
          <div className="transcript-skeleton">{generateSkeletonBars()}</div>
        ) : (
          <>
            <p
              ref={conversationRef}
              className={classNames('transcript-conversation', {
                highlighted,
                // Marks the turn as the one being commented on, so the page
                // reads as focused on it for the length of the interaction.
                'transcript-conversation--commenting': commentActive || commentFlowActive,
                // Text on a commentable turn is inert until the affordance is
                // pressed, so a stray drag can never start a comment — or, as
                // it used to, restart the audio.
                'transcript-conversation--locked': showCommentLayer && !armed,
                // Armed: selectable, with the ring and sweep that show what
                // to do. Shared with the prompt surface so the gesture is
                // taught identically on both.
                'comment-armed': armed && !composerOpen,
              })}
            >
              {conversation}
            </p>
            {armed && !composerOpen && (
              <span
                className="comment-select-hint-label"
                data-testid={`transcript-comment-hint-${turnStartMs}`}
              >
                Select the words you want to comment on
              </span>
            )}
            {showCommentLayer && (
              <SelectionCommentPopover
                containerRef={conversationRef}
                baseText={fullText}
                enabled={!otherTurnComposing}
                busy={commentsConfig.busy}
                onSubmit={({ start, end, snippet, message }) =>
                  commentsConfig.onCreate({
                    turnStartMs,
                    highlightStart: start,
                    highlightEnd: end,
                    highlightSnippet: snippet,
                    message,
                  })
                }
                existingRanges={turnMarks}
                onOpenExisting={commentsConfig.onMarkClick}
                onPendingChange={setDraftRange}
                onActiveChange={handleCommentActiveChange}
                onComposingChange={handleCommentComposingChange}
                requiresArming
                armed={armed}
                onDisarm={handleDisarm}
              />
            )}
            {showHypothesis && hasHypothesis && (
              <>
                <p className="hypothesis-label">HYPOTHESIS</p>
                <p className="transcript-hypothesis">{hypothesis}</p>
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}
